import { fetchEventSource } from '@microsoft/fetch-event-source';
import { apiConfig } from '../../../config/config';
import { getBilledEntityIds } from '../../services/getBilledEntityIds';
import type {
  ChatStreamRequest,
  StreamCallbacks,
  CreateSessionRequest,
  SessionResponse,
  SessionDetailResponse,
  SSEErrorEvent,
  TextEvent,
  DoneEvent,
  ToolCallEvent,
  ThinkingEvent,
  UiBlockEvent,
  AgentInfo,
} from './types';

const BASE = (): string => {
  const url = apiConfig.agentForgeEndpoint;
  if (!url) {
    throw new Error('AGENT_FORGE_URL is not configured. Check your environment config.');
  }
  return url;
};

function buildHeaders(token: string): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const billedEntityIds = getBilledEntityIds();
  if (billedEntityIds) headers['x-billed-entity-ids'] = billedEntityIds;
  return headers;
}

export async function streamChatMessage(request: ChatStreamRequest, token: string, callbacks: StreamCallbacks, signal?: AbortSignal): Promise<void> {
  await fetchEventSource(`${BASE()}/api/chat/stream`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(request),
    signal,
    onmessage(msg) {
      switch (msg.event) {
        case 'text':
          callbacks.onToken(JSON.parse(msg.data) as TextEvent);
          break;
        case 'done':
          callbacks.onDone(JSON.parse(msg.data) as DoneEvent);
          break;
        case 'error': {
          const parsed = JSON.parse(msg.data) as SSEErrorEvent;
          callbacks.onError(new Error(parsed.error));
          break;
        }
        case 'tool_call': {
          const raw = JSON.parse(msg.data);
          const normalized: ToolCallEvent = raw.tool_name
            ? raw
            : {
                tool_name: raw.tool ?? 'unknown',
                tool_call_id: raw.tool_call_id ?? crypto.randomUUID(),
                display_name: raw.display_name ?? raw.tool ?? 'Tool',
                category: raw.category ?? 'default',
                status: raw.status === 'completed' ? 'completed' : 'running',
              };
          callbacks.onToolCall?.(normalized);
          break;
        }
        case 'thinking':
          callbacks.onThinking?.(JSON.parse(msg.data) as ThinkingEvent);
          break;
        case 'ui_block':
          callbacks.onUiBlock?.(JSON.parse(msg.data) as UiBlockEvent);
          break;
      }
    },
    onerror(err) {
      // Do not retry on 401/403 — rethrow to stop reconnection
      throw err;
    },
  });
}

export async function createSession(request: CreateSessionRequest, token: string): Promise<SessionResponse> {
  const res = await fetch(`${BASE()}/api/sessions`, {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Create session failed: ${res.status}`);
  const json = await res.json();
  return json.data as SessionResponse;
}

export async function fetchSessions(token: string): Promise<SessionResponse[]> {
  const res = await fetch(`${BASE()}/api/sessions`, {
    headers: buildHeaders(token),
  });
  if (!res.ok) throw new Error(`Fetch sessions failed: ${res.status}`);
  const json = await res.json();
  return json.data as SessionResponse[];
}

export async function fetchAgents(token: string): Promise<AgentInfo[]> {
  const res = await fetch(`${BASE()}/api/agents`, {
    headers: buildHeaders(token),
  });
  if (!res.ok) throw new Error(`Fetch agents failed: ${res.status}`);
  const json = await res.json();
  // API returns { success: true, data: { agent_name: { name, title, description }, ... } }
  return Object.values(json.data) as AgentInfo[];
}

export async function fetchSessionHistory(sessionId: string, token: string): Promise<SessionDetailResponse> {
  const res = await fetch(`${BASE()}/api/sessions/${sessionId}`, {
    headers: buildHeaders(token),
  });
  if (!res.ok) throw new Error(`Fetch session failed: ${res.status}`);
  const json = await res.json();
  return json.data as SessionDetailResponse;
}
